/* eslint-disable no-undef */
import React, { FC, memo, useContext, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { MapContext, MapContextProvider } from '../../context/MapContext';
import { UiStateContext } from '../../context/UiStateContext';
import SchoolMapMarker from '../../images/maps/marker-school-red.png';
import { Kfh_AreaType, Kfh_ArtirixSchoolItemType, Kfh_CoordinateType } from '../../types/graphqlTypes';
import { addHttp } from '../../utils/addHttp';
import { ExpanderPanel } from '../ExpanderPanel';
import { ExpanderPanelGroup } from '../ExpanderPanelGroup';
import { Radio } from '../forms/Radio';
import { IconLink } from '../IconLink';
import { SideAndMain, StyledSide } from '../layout/SideAndMain';
import { Map, MapMarker } from '../Map';
import { PageBackButton } from '../PageBackButton';
import { Section } from '../Section';
import { SEO } from '../Seo';
import { Spacer } from '../Spacer';
import { Typography } from '../Typography';
import { LocationActionSection } from './LocationActionSection';
import { LocationNavigation } from './LocationNavigation';
import { SchoolBlade } from './SchoolBlade';

interface LocationSchoolsProps {
  location: Kfh_AreaType;
}

interface SchoolProps {
  school: Kfh_ArtirixSchoolItemType;
}

interface SchoolsListProps {
  name: string;
  schools: Kfh_ArtirixSchoolItemType[];
  open?: boolean;
  polygon?: Kfh_CoordinateType[];
}

const StyledRadioBlock = styled.div`
  ${({ theme: { space, media } }) => css`
    display: flex;
    gap: ${space.sm};
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    overflow-y: auto;
    max-width: 100%;

    @media (min-width: ${media.md}) {
      flex-wrap: nowrap;
      flex-direction: row;
      gap: ${space.md};
    }
  `}
`;

const StyledSchool = styled.li`
  ${({ theme: { media, space, borders } }) => css`
    display: grid;
    gap: ${space.md};

    a {
      display: block;
      text-decoration: underline;
    }

    // Desktop

    @media (min-width: ${media.md}) {
      gap: ${space.xl};
      grid-template-columns: repeat(2, 1fr);

      & + & {
        margin-top: ${space.lg};
        padding-top: ${space.lg};
        border-top: ${borders.default};
      }
    }
  `};
`;

const School: FC<SchoolProps> = ({ school }) => {
  const { map } = useContext(MapContext);
  const {
    name,
    schoolAddress,
    schoolUrl,
    establishmentType,
    religiousCharacter,
    gender,
    minAge,
    maxAge,
    latitude,
    longitude,
  } = school;

  const handleScrollToMarker = () => {
    if (map) {
      const mapRef = map.getDiv();
      if (mapRef) {
        mapRef.scrollIntoView({
          behavior: 'smooth',
        });
        map.setCenter({ lat: latitude, lng: longitude });
        map.setZoom(18);
      }
    }
  };

  return (
    <StyledSchool>
      <Spacer spacing="half">
        <Typography component="h3" variant="titleMedium">
          {name}
        </Typography>
        <address>
          {schoolAddress.streetname}
          <br />
          {schoolAddress.town}
          <br />
          {schoolAddress.postcode}
          <br />
          <IconLink arrowRight as="button" label="Show on map" onClick={() => handleScrollToMarker()} />
        </address>
        <a href={addHttp(schoolUrl)} target="_blank" rel="noreferrer">
          {schoolUrl}
        </a>
      </Spacer>
      <Spacer spacing="quarter" as="dl">
        <div>
          <Typography component="dt" variant="titleSmall">
            Type
          </Typography>
          <dd>{establishmentType}</dd>
        </div>
        <div>
          <Typography component="dt" variant="titleSmall">
            Religious Character
          </Typography>
          <dd>{religiousCharacter}</dd>
        </div>
        <div>
          <Typography component="dt" variant="titleSmall">
            Age range
          </Typography>
          <dd>
            {minAge} to {maxAge} years
          </dd>
        </div>
        <div>
          <Typography component="dt" variant="titleSmall">
            Gender
          </Typography>
          <dd>{gender}</dd>
        </div>
      </Spacer>
    </StyledSchool>
  );
};
const SchoolsList: FC<SchoolsListProps> = memo(({ name, schools, open }) => {
  if (!schools || schools.length < 1) {
    return null;
  }

  return (
    <ExpanderPanel title={`${name} (${schools.length})`} as="li" headingComponent="h2" open={open}>
      <ul>
        {schools?.map((school) => (
          <School school={school} key={school.id} />
        ))}
      </ul>
    </ExpanderPanel>
  );
});

const iconComponent = SchoolMapMarker;

const SchoolMainContent = ({
  schools,
  heading,
  polygon,
}: {
  schools: Kfh_ArtirixSchoolItemType[];
  heading: string;
}) => {
  const [activeSchool, setActiveSchool] = useState<Kfh_ArtirixSchoolItemType>();
  const [activeSchoolType, setActiveSchoolType] = useState('all');
  const { setActiveBlade } = useContext(UiStateContext);

  const primarySchools = schools?.filter((school) => school.educationPhase === 'Primary');
  const secondarySchools = schools?.filter((school) => school.educationPhase === 'Secondary');
  const nurserySchools = schools?.filter((school) => school.educationPhase === 'Nursery');
  const otherSchools = schools?.filter((school) => school.educationPhase === 'Other');

  const allSchools = useMemo(
    () => ({
      all: schools,
      primarySchools,
      secondarySchools,
      nurserySchools,
      otherSchools,
    }),
    [nurserySchools, otherSchools, primarySchools, schools, secondarySchools]
  );

  type SchoolTypeKeys = keyof typeof allSchools;

  const mapMarkers: MapMarker[] = useMemo(() => {
    const handleOpenBlade = (schoolToShow: Kfh_ArtirixSchoolItemType) => {
      setActiveSchool(schoolToShow);
      setActiveBlade('school');
    };
    const theSchools = [...allSchools[activeSchoolType]];
    const filtertedSchools = theSchools.map((school) => ({
      position: {
        lat: +school.latitude + Math.random() / 2500,
        lng: +school.longitude + Math.random() / 2500,
      },
      id: school.id,
      onClick: () => handleOpenBlade(school),
      iconComponent,
    }));

    return filtertedSchools;
  }, [activeSchoolType]);

  const handlSetActiveSchoolType = (schoolType: SchoolTypeKeys) => {
    setActiveSchoolType(schoolType);
  };

  return (
    <>
      <Spacer responsive>
        {/* Heading */}

        <Typography component="h1" variant="displaySmall">
          {heading}
        </Typography>

        {/* Map */}
        <Spacer>
          <StyledRadioBlock>
            <Radio
              label="All"
              id="schools-all"
              value="all"
              name="schoolType"
              checked={activeSchoolType === 'all'}
              onChange={() => handlSetActiveSchoolType('all')}
            />
            {primarySchools && primarySchools.length > 0 && (
              <Radio
                label="Primary"
                id="schools-Primary"
                value="primarySchools"
                name="schoolType"
                checked={activeSchoolType === 'primarySchools'}
                onChange={() => handlSetActiveSchoolType('primarySchools')}
              />
            )}
            {secondarySchools && secondarySchools.length > 0 && (
              <Radio
                label="Secondary"
                id="schools-Secondary"
                value="secondarySchools"
                name="schoolType"
                checked={activeSchoolType === 'secondarySchools'}
                onChange={() => handlSetActiveSchoolType('secondarySchools')}
              />
            )}
            {nurserySchools && nurserySchools.length > 0 && (
              <Radio
                label="Nursery"
                id="schools-Nursery"
                value="nurserySchools"
                name="schoolType"
                checked={activeSchoolType === 'nurserySchools'}
                onChange={() => handlSetActiveSchoolType('nurserySchools')}
              />
            )}
            {otherSchools && otherSchools.length > 0 && (
              <Radio
                label="Other"
                id="schools-Other"
                value="otherSchools"
                name="schoolType"
                checked={activeSchoolType === 'otherSchools'}
                onChange={() => handlSetActiveSchoolType('otherSchools')}
              />
            )}
          </StyledRadioBlock>
          <Map markers={mapMarkers} mapStyle="schools" polygon={polygon} />
        </Spacer>
        {/* Schools */}

        {(primarySchools && primarySchools.length > 0) ||
        (secondarySchools && secondarySchools.length) ||
        (nurserySchools && nurserySchools.length) ||
        (otherSchools && otherSchools.length > 0) ? (
          <ExpanderPanelGroup as="ul">
            <SchoolsList name="Primary" schools={primarySchools} open />
            <SchoolsList name="Secondary" schools={secondarySchools} />
            <SchoolsList name="Nursery" schools={nurserySchools} />
            <SchoolsList name="Other" schools={otherSchools} />
          </ExpanderPanelGroup>
        ) : (
          <>No school data available</>
        )}
      </Spacer>
      {activeSchool && <SchoolBlade school={activeSchool} />}
    </>
  );
};

const LocationSchools: FC<LocationSchoolsProps> = ({ location }) => {
  const { name, relativeUrl, seoPages, coordinates } = location;
  const seo = seoPages.find((seoPage) => seoPage.remoteTypeName === 'KfhSeoAreaGuideSchoolType');
  const schools = location.locationSchoolData.schoolData;

  const defaultMultisearch = location.artirixMultiSearch
    ? {
        name: location.artirixDisplayName,
        multiSearch: location.artirixMultiSearch,
      }
    : {};

  return (
    <>
      {/* Back button */}

      <PageBackButton url={relativeUrl} label={`${name} overview`} />

      <Section contained>
        <SideAndMain>
          {/* Main content */}
          <MapContextProvider>
            {schools ? (
              <SchoolMainContent
                schools={schools}
                polygon={coordinates}
                heading={seo?.h1 ? seo.h1 : `${name} Schools`}
              />
            ) : (
              'Sorry, we currently have no school data'
            )}
          </MapContextProvider>
          {/* Side navigation */}
          <StyledSide>
            <LocationNavigation url={relativeUrl} name={name} isArea />
          </StyledSide>
        </SideAndMain>
      </Section>

      {/* Actions */}
      <LocationActionSection defaultMultisearch={defaultMultisearch} />
    </>
  );
};

export default LocationSchools;
